import type { RouteTabConfig } from '@zen/Components/RouteTabs';
import ProductSettings from '@zen/Products/ProductSettings';
import { accountSettingsRoutes } from '@zen/Routes';

import AccountConnections from '../AccountDetails/AccountConnections';
import AccountLocations from '../AccountDetails/AccountLocations';
import AccountMembers from '../AccountDetails/AccountMembers';
import BusinessUnits from '../AccountDetails/BusinessUnits';
import GeneralAccountDetails from '../AccountDetails/GeneralAccountDetails';
import SOPs from '../AccountDetails/SOPs/SOPs';
import { type Account, AccountStatus } from '../AccountDetails/types';
import Webhooks from '../AccountDetails/Webhooks';
import Authentication from '../Authentication';

interface AccountSettingsPermissions {
  canViewAccountGeneral: boolean;
  canViewAuthentication: boolean;
  canViewBusinessUnits: boolean;
  canViewConnections: boolean;
  canViewLocations: boolean;
  canViewMembers: boolean;
  canViewProductSettings: boolean;
  canViewSOPs: boolean;
  canViewWebhooks: boolean;
}

const getTabsConfig = (
  accountDetails: Account,
  {
    canViewAccountGeneral,
    canViewAuthentication,
    canViewBusinessUnits,
    canViewConnections,
    canViewLocations,
    canViewMembers,
    canViewProductSettings,
    canViewSOPs,
    canViewWebhooks
  }: AccountSettingsPermissions
): RouteTabConfig[] => {
  const { id: accountId, permissions, ssoDetails, tradingName, organizationalRole } = accountDetails;

  const accountSettingsPermissions = permissions?.settings;

  const businessUnits: RouteTabConfig = {
    path: accountSettingsRoutes.businessUnits.relativePath,
    label: 'Business units',
    component: (
      <BusinessUnits
        accountId={accountId}
        accountReferencePrefix={accountDetails.referencePrefix}
        permissions={accountSettingsPermissions}
      />
    ),
    url: accountSettingsRoutes.businessUnits.getUrl()
  };

  const general: RouteTabConfig = {
    path: accountSettingsRoutes.generalInformation.relativePath,
    label: 'General',
    component: <GeneralAccountDetails accountDetails={accountDetails} />,
    url: accountSettingsRoutes.generalInformation.getUrl()
  };

  const members: RouteTabConfig = {
    path: accountSettingsRoutes.members.relativePath,
    label: 'Members',
    component: <AccountMembers accountId={accountId} permissions={accountSettingsPermissions} />,
    url: accountSettingsRoutes.members.getUrl()
  };

  const locations: RouteTabConfig = {
    path: accountSettingsRoutes.locations.relativePath,
    label: 'Locations',
    component: (
      <AccountLocations
        accountId={accountId}
        accountIsArchived={accountDetails.status === AccountStatus.INACTIVE}
        organizationalRole={organizationalRole}
        permissions={accountSettingsPermissions}
      />
    ),
    url: accountSettingsRoutes.locations.getUrl()
  };

  const connections: RouteTabConfig = {
    path: accountSettingsRoutes.connections.relativePath,
    label: 'Connections',
    component: <AccountConnections accountId={accountId} permissions={accountSettingsPermissions} />,
    url: accountSettingsRoutes.connections.getUrl()
  };

  const authentication: RouteTabConfig = {
    path: accountSettingsRoutes.authentication.relativePath,
    label: 'Authentication',
    component: (
      <Authentication
        accountId={accountId}
        accountName={tradingName}
        permissions={accountSettingsPermissions}
        ssoDetails={ssoDetails}
      />
    ),
    url: accountSettingsRoutes.authentication.getUrl()
  };

  const productSettings: RouteTabConfig = {
    path: accountSettingsRoutes.productSettings.relativePath,
    label: 'Product settings',
    component: (
      <ProductSettings
        accountId={accountId}
        canCreateProductAttribute={!!accountSettingsPermissions?.canCreateProductAttribute?.value}
        canCreateProductCategory={!!accountSettingsPermissions?.canCreateProductCategory?.value}
      />
    ),
    url: accountSettingsRoutes.productSettings.getUrl()
  };

  const webhooks: RouteTabConfig = {
    path: accountSettingsRoutes.webhooks.relativePath,
    label: 'Webhooks',
    component: <Webhooks accountId={accountId} permissions={accountSettingsPermissions} />,
    url: accountSettingsRoutes.webhooks.getUrl()
  };

  const sops: RouteTabConfig = {
    path: accountSettingsRoutes.sops.relativePath,
    label: 'SOPs',
    component: <SOPs accountId={accountId} canCreateSOP={accountSettingsPermissions?.canCreateSOP?.value} />,
    url: accountSettingsRoutes.sops.getUrl()
  };

  const tabs = [];

  if (canViewAccountGeneral) {
    tabs.push(general);
  }

  if (canViewMembers) {
    tabs.push(members);
  }

  if (canViewLocations) {
    tabs.push(locations);
  }

  if (canViewBusinessUnits) {
    tabs.push(businessUnits);
  }

  if (canViewConnections) {
    tabs.push(connections);
  }

  if (canViewWebhooks) {
    tabs.push(webhooks);
  }

  if (canViewAuthentication) {
    tabs.push(authentication);
  }

  if (canViewProductSettings) {
    tabs.push(productSettings);
  }

  if (canViewSOPs) {
    tabs.push(sops);
  }

  return tabs;
};

export { getTabsConfig };
